import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, DollarSign, Calendar, Users,
  ArrowRight, Eye
} from 'lucide-react';
import { db, auth } from '../../config/firebase-config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_SLOTS = ['8-10 AM', '10-12 PM', '12-2 PM', '2-4 PM', '4-6 PM', '6-8 PM'];

const Analytics = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState('all');
  const [chartTab, setChartTab] = useState('revenue');
  const userTier = localStorage.getItem('userTier') || 'free';

  const [loading, setLoading] = useState(true);
  const [allBookings, setAllBookings] = useState([]);
  const [siteViews, setSiteViews] = useState({});

  useEffect(() => {
    if (!db || !auth) {
      setLoading(false);
      return;
    }

    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setLoading(false); }
    }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        try {
          const q = query(collection(db, 'bookings'), where('practitionerId', '==', user.uid));
          const snap = await getDocs(q);
          setAllBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));

          const practDoc = await getDoc(doc(db, 'practitioners', user.uid));
          if (practDoc.exists()) setSiteViews(practDoc.data().siteViews || {});
        } catch (err) {
          console.error('Analytics: Error loading data:', err);
        }
        setLoading(false);
      }
    });

    return () => { done = true; clearTimeout(timeout); unsubscribe(); };
  }, []);

  const getBookingDate = (b) =>
    b.dateTime?.toDate ? b.dateTime.toDate() : new Date(b.dateTime);

  const filteredBookings = useMemo(() => {
    const now = new Date();
    let cutoff = null;
    let endDate = null;
    switch (timePeriod) {
      case '7days':    cutoff = new Date(now - 7 * 86400000); break;
      case '30days':   cutoff = new Date(now - 30 * 86400000); break;
      case '90days':   cutoff = new Date(now - 90 * 86400000); break;
      case 'year':     cutoff = new Date(now.getFullYear(), 0, 1); break;
      case 'lastYear':
        cutoff = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear(), 0, 1);
        break;
      default: break;
    }
    return allBookings.filter(b => {
      const d = getBookingDate(b);
      if (cutoff && d < cutoff) return false;
      if (endDate && d >= endDate) return false;
      return true;
    });
  }, [allBookings, timePeriod]);

  const analytics = useMemo(() => {
    if (filteredBookings.length === 0) return null;

    const totalRevenue = filteredBookings.reduce((s, b) => s + (b.service?.price || 0), 0);
    const totalSessions = filteredBookings.length;
    const uniqueEmails = new Set(
      filteredBookings.map(b => b.client?.email?.toLowerCase()).filter(Boolean)
    );
    const totalClients = uniqueEmails.size;
    const avgSessionCost = totalSessions > 0 ? totalRevenue / totalSessions : 0;

    // Revenue by month
    const monthMap = {};
    filteredBookings.forEach(b => {
      const d = getBookingDate(b);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { revenue: 0, date: d };
      monthMap[key].revenue += (b.service?.price || 0);
    });
    const revenueByMonth = Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, val]) => ({
        month: val.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        revenue: val.revenue
      }));

    // Top services
    const serviceMap = {};
    filteredBookings.forEach(b => {
      const name = b.service?.name || 'Unknown';
      if (!serviceMap[name]) serviceMap[name] = { sessions: 0, revenue: 0 };
      serviceMap[name].sessions++;
      serviceMap[name].revenue += (b.service?.price || 0);
    });
    const topServices = Object.entries(serviceMap)
      .map(([service, data]) => ({
        service, ...data,
        percentage: totalRevenue > 0 ? Math.round((data.revenue / totalRevenue) * 100) : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Bookings by day of week
    const dayCount = Object.fromEntries(DAY_NAMES.map(d => [d, 0]));
    filteredBookings.forEach(b => { dayCount[DAY_NAMES[getBookingDate(b).getDay()]]++; });
    const bookingsByDayOfWeek = DAY_NAMES.map(day => ({ day, bookings: dayCount[day] }));

    // Bookings by time of day
    const timeCount = Object.fromEntries(TIME_SLOTS.map(t => [t, 0]));
    filteredBookings.forEach(b => {
      const h = getBookingDate(b).getHours();
      if (h >= 8 && h < 10)   timeCount['8-10 AM']++;
      else if (h >= 10 && h < 12) timeCount['10-12 PM']++;
      else if (h >= 12 && h < 14) timeCount['12-2 PM']++;
      else if (h >= 14 && h < 16) timeCount['2-4 PM']++;
      else if (h >= 16 && h < 18) timeCount['4-6 PM']++;
      else if (h >= 18 && h < 20) timeCount['6-8 PM']++;
    });
    const bookingsByTimeOfDay = TIME_SLOTS.map(time => ({ time, bookings: timeCount[time] }));

    // Client retention: new = first booking is within current period
    const now = new Date();
    let periodStart = null;
    switch (timePeriod) {
      case '7days':  periodStart = new Date(now - 7 * 86400000); break;
      case '30days': periodStart = new Date(now - 30 * 86400000); break;
      case '90days': periodStart = new Date(now - 90 * 86400000); break;
      case 'year':   periodStart = new Date(now.getFullYear(), 0, 1); break;
      case 'lastYear': periodStart = new Date(now.getFullYear() - 1, 0, 1); break;
      default: break;
    }
    const clientFirstBooking = {};
    allBookings.forEach(b => {
      const email = b.client?.email?.toLowerCase();
      if (!email) return;
      const d = getBookingDate(b);
      if (!clientFirstBooking[email] || d < clientFirstBooking[email]) clientFirstBooking[email] = d;
    });
    let newClients = 0, returningClients = 0;
    uniqueEmails.forEach(email => {
      if (!periodStart || clientFirstBooking[email] >= periodStart) newClients++;
      else returningClients++;
    });
    if (!periodStart) { newClients = totalClients; returningClients = 0; }
    const retentionRate = totalClients > 0 ? Math.round((returningClients / totalClients) * 100) : 0;

    return {
      overview: { totalRevenue, totalSessions, totalClients, avgSessionCost },
      revenueByMonth, topServices, bookingsByDayOfWeek, bookingsByTimeOfDay,
      clientRetention: { newClients, returningClients, retentionRate }
    };
  }, [filteredBookings, allBookings, timePeriod]);

  const siteViewsByMonth = useMemo(() =>
    Object.entries(siteViews)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, views]) => {
        const [year, month] = key.split('-');
        const d = new Date(parseInt(year), parseInt(month) - 1, 1);
        return { month: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), views };
      }),
  [siteViews]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  const EMPTY_MSG = 'No booking data yet — your analytics will appear here as clients book sessions';
  const EMPTY_VIEWS_MSG = 'No site view data yet — views will be tracked once your site is published';

  const EmptyChart = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <p className="text-sm text-gray-500 max-w-xs">{message}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-40" />
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 h-28" />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 h-64" />
              <div className="bg-white rounded-xl border border-gray-200 h-64" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400" aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">Analytics</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm text-gray-600">Track your business performance and trends</p>
            <p className="text-sm text-gray-600 mt-1">
              Need to export data for taxes?{' '}
              <span
                onClick={() => navigate('/dashboard/reports')}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/reports'); }}
                className="text-purple-600 hover:text-purple-700 hover:underline font-medium inline-flex items-center gap-1 cursor-pointer"
              >
                View Reports <ArrowRight className="w-3 h-3" />
              </span>
            </p>
          </div>
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">This Year</option>
            <option value="lastYear">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(analytics?.overview.totalRevenue ?? 0)}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-purple-100 rounded-full p-3">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Sessions</p>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.overview.totalSessions ?? 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-blue-100 rounded-full p-3">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Clients</p>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.overview.totalClients ?? 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-amber-100 rounded-full p-3">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Avg Session Cost</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(analytics?.overview.avgSessionCost ?? 0)}
            </p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Trend / Site Views with tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setChartTab('revenue')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    chartTab === 'revenue' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Revenue
                </button>
                <button
                  onClick={() => setChartTab('siteViews')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    chartTab === 'siteViews' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  Site Views
                </button>
              </div>
            </div>

            {chartTab === 'revenue' ? (
              analytics?.revenueByMonth?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.revenueByMonth.map(item => {
                    const maxRev = Math.max(...analytics.revenueByMonth.map(i => i.revenue));
                    const width = maxRev > 0 ? (item.revenue / maxRev) * 100 : 0;
                    return (
                      <div key={item.month}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.month}</span>
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(item.revenue)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-3 rounded-full transition-all" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <EmptyChart message={EMPTY_MSG} />
            ) : (
              siteViewsByMonth.length > 0 ? (
                <div className="space-y-3">
                  {siteViewsByMonth.map(item => {
                    const maxViews = Math.max(...siteViewsByMonth.map(i => i.views));
                    const width = maxViews > 0 ? (item.views / maxViews) * 100 : 0;
                    return (
                      <div key={item.month}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.month}</span>
                          <span className="text-sm font-semibold text-gray-900">{item.views.toLocaleString()} views</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full transition-all" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <EmptyChart message={EMPTY_VIEWS_MSG} />
            )}
          </div>

          {/* Top Services */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Top Services</h3>
            {analytics?.topServices?.length > 0 ? (
              <div className="space-y-4">
                {analytics.topServices.map(service => (
                  <div key={service.service}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{service.service}</p>
                        <p className="text-sm text-gray-600">
                          {service.sessions} sessions • {service.percentage}% of revenue
                        </p>
                      </div>
                      <p className="font-bold text-gray-900">{formatCurrency(service.revenue)}</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="h-2 rounded-full" style={{ width: `${service.percentage}%`, background: 'linear-gradient(90deg, #7c3aed, #22d3ee)' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyChart message={EMPTY_MSG} />}
          </div>
        </div>

        {/* Booking Patterns Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Bookings by Day */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Bookings by Day of Week</h3>
            {analytics?.bookingsByDayOfWeek?.some(d => d.bookings > 0) ? (
              <div className="flex items-end justify-between gap-2 h-48">
                {analytics.bookingsByDayOfWeek.map(item => {
                  const maxBookings = Math.max(...analytics.bookingsByDayOfWeek.map(i => i.bookings));
                  const height = maxBookings > 0 ? (item.bookings / maxBookings) * 100 : 0;
                  return (
                    <div key={item.day} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full flex items-end justify-center" style={{ height: '160px' }}>
                        <div
                          className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t-lg transition-all hover:from-purple-700 hover:to-purple-500"
                          style={{ height: `${height}%`, minHeight: item.bookings > 0 ? '20px' : '0' }}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-semibold text-gray-900">{item.bookings}</p>
                        <p className="text-xs text-gray-600">{item.day}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyChart message={EMPTY_MSG} />}
          </div>

          {/* Bookings by Time */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Bookings by Time of Day</h3>
            {analytics?.bookingsByTimeOfDay?.some(t => t.bookings > 0) ? (
              <div className="space-y-3">
                {analytics.bookingsByTimeOfDay.map(item => {
                  const maxBookings = Math.max(...analytics.bookingsByTimeOfDay.map(i => i.bookings));
                  const width = maxBookings > 0 ? (item.bookings / maxBookings) * 100 : 0;
                  return (
                    <div key={item.time}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{item.time}</span>
                        <span className="text-sm font-semibold text-gray-900">{item.bookings} bookings</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ background: 'linear-gradient(90deg, #7c3aed, #22d3ee)', width: `${width}%`, minWidth: item.bookings > 0 ? '8px' : '0' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyChart message={EMPTY_MSG} />}
          </div>
        </div>

        {/* Client Retention */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Client Retention</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600 mb-1">
                {analytics?.clientRetention.newClients ?? 0}
              </p>
              <p className="text-sm text-gray-700">New Clients</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600 mb-1">
                {analytics?.clientRetention.returningClients ?? 0}
              </p>
              <p className="text-sm text-gray-700">Returning Clients</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600 mb-1">
                {analytics?.clientRetention.retentionRate ?? 0}%
              </p>
              <p className="text-sm text-gray-700">Retention Rate</p>
            </div>
          </div>
        </div>

        {/* Elite Feature Teaser - Pro Users */}
        {userTier === 'pro' && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <span className="text-4xl">📊</span>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">Unlock Advanced Analytics with Elite</h3>
                <p className="text-gray-700 mb-4">
                  Get AI-powered insights including revenue forecasting, client lifetime value predictions,
                  personalized growth recommendations, and competitive benchmarking.
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Upgrade to Elite
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
